import type { AppError } from '@/types'
import { Banner, Button, Card, StatusChip } from '@/components/ui'
import { CheckIcon } from '@/components/icons'
import type { BreathingExercise } from '../exercises'
import { formatDurationNl } from '../timing'

export type SessionSaveOutcome = 'pending' | 'saved' | 'not_configured' | 'failed'

export type CompletionViewProps = {
  kind: 'completed' | 'interrupted'
  exercise: BreathingExercise
  elapsedSeconds: number
  saveOutcome: SessionSaveOutcome
  saveError: AppError | null
  onAgain: () => void
  onOverview: () => void
}

/** Completion / stopped view with an honest storage state for the result. */
export function CompletionView({
  kind,
  exercise,
  elapsedSeconds,
  saveOutcome,
  saveError,
  onAgain,
  onOverview,
}: CompletionViewProps) {
  const completed = kind === 'completed'

  return (
    <div className="breathing-completion">
      <Card padding="lg" className="breathing-completion__card">
        <span className="breathing-completion__icon" aria-hidden="true">
          <CheckIcon size={32} />
        </span>
        <h2 className="breathing-completion__title">
          {completed ? 'Goed gedaan' : 'Oefening gestopt'}
        </h2>
        <p className="breathing-completion__body">
          {completed
            ? `Je hebt ${formatDurationNl(elapsedSeconds)} geoefend met ${exercise.name}.`
            : `Je hebt ${formatDurationNl(elapsedSeconds)} geoefend met ${exercise.name}. Stoppen is altijd oké.`}
        </p>
        <StatusChip status={completed ? 'success' : 'neutral'}>
          {completed ? 'Afgerond' : 'Gestopt'}
        </StatusChip>

        <div className="breathing-completion__save" aria-live="polite">
          {saveOutcome === 'pending' && (
            <p className="breathing-completion__save-note">Je sessie wordt opgeslagen…</p>
          )}
          {saveOutcome === 'saved' && (
            <p className="breathing-completion__save-note">
              Je sessie is opgeslagen in je geschiedenis.
            </p>
          )}
        </div>

        {saveOutcome === 'not_configured' && (
          <Banner tone="warning">
            Je resultaat is niet in de cloud opgeslagen: de cloudverbinding is niet
            ingesteld. De oefening zelf werkt gewoon.
          </Banner>
        )}
        {saveOutcome === 'failed' && (
          <Banner tone="error">
            Je resultaat kon niet worden opgeslagen.
            {saveError ? ` ${saveError.userMessage}` : ''}
          </Banner>
        )}

        <div className="breathing-completion__actions">
          <Button variant="primary" size="lg" fullWidth onClick={onAgain}>
            Opnieuw oefenen
          </Button>
          <Button variant="secondary" size="lg" fullWidth onClick={onOverview}>
            Terug naar overzicht
          </Button>
        </div>
      </Card>
    </div>
  )
}
